import { Network } from '@puzzlehq/sdk';
import { Page } from '@src/components/layout/Page';
import { trpcReact } from '@src/data/trpc';
import {
  getIsGiveawayClosed,
  getIsGiveawayOver,
} from '@src/hooks/useGiveaways';
import { createFileRoute } from '@tanstack/react-router';
import { getCountryData, getEmojiFlag } from 'countries-list';
import {
  GiveawayOpenStatus,
  GiveawayOperatorStatus,
  RaffleV2,
  GiveawayStatus,
  supportedCountryCodes,
  SupportedCountyCodes,
} from 'jigsaw';
import { HStack, Skeleton, Text, VStack } from 'ui_2';
import { FilterButtonOption } from '@src/components/FilterButtons';
import { useState, useMemo } from 'react';
import { GiveawayEntriesGraphModal } from '@src/components/operatoor/GiveawayEntriesGraphModal';
import {
  GiveawayCard,
  GiveawayCardProps,
} from '@src/components/operatoor/GiveawayStatusCard';

export const Route = createFileRoute('/operatoor/giveaways')({
  component: RouteComponent,
});

const getErrorMessage = (status?: GiveawayOperatorStatus) => {
  if (!status) return;
  switch (status) {
    case GiveawayOperatorStatus.drawFailed:
      return 'Drawing Failed';
    case GiveawayOperatorStatus.mintFailed:
      return 'Minting Failed';
    case GiveawayOperatorStatus.sendingFailed:
      return 'Sending Prizes Failed';
  }
};

const openStatusFilter: FilterButtonOption[] = [
  {
    label: 'Upcoming',
    value: GiveawayOpenStatus.upcoming,
  },
  {
    label: 'Ongoing',
    value: GiveawayOpenStatus.ongoing,
  },
  {
    label: 'Closed',
    value: GiveawayOpenStatus.closed,
  },
  {
    label: 'Drawn',
    value: GiveawayOpenStatus.drawn,
  },
  {
    label: 'Ended',
    value: GiveawayOpenStatus.ended,
  },
];

const operatoorStatusFilter: FilterButtonOption[] = [
  {
    label: 'Idle',
    value: GiveawayOperatorStatus.idle,
  },
  {
    label: 'Minting',
    value: GiveawayOperatorStatus.minting,
  },
  {
    label: 'Minted',
    value: GiveawayOperatorStatus.minted,
  },
  {
    label: 'Mint Failed',
    value: GiveawayOperatorStatus.mintFailed,
  },
  {
    label: 'Drawing',
    value: GiveawayOperatorStatus.drawing,
  },
  {
    label: 'Drawn',
    value: GiveawayOperatorStatus.drawn,
  },
  {
    label: 'Draw Failed',
    value: GiveawayOperatorStatus.drawFailed,
  },
  {
    label: 'Sending Prizes',
    value: GiveawayOperatorStatus.sendingPrizes,
  },
  {
    label: 'Sent Prizes',
    value: GiveawayOperatorStatus.sentPrizes,
  },
  {
    label: 'Sending Failed',
    value: GiveawayOperatorStatus.sendingFailed,
  },
];

type SortField = 'cutoff' | 'uniqueEntries';
type SortOrder = 'asc' | 'desc';

// --- Main RouteComponent ---
function RouteComponent() {
  const [selectedOpenStatus, setSelectedOpenStatus] = useState<
    GiveawayOpenStatus[]
  >([GiveawayOpenStatus.ongoing]);
  const [selectedOperatorStatus, setSelectedOperatorStatus] = useState<
    GiveawayOperatorStatus[]
  >([]);
  const [selectedCountries, setSelectedCountries] = useState<
    SupportedCountyCodes[]
  >([]);
  const [sortBy, setSortBy] = useState<SortField>('cutoff');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  // Modal state for graph
  const [selectedGiveaway, setSelectedGiveaway] = useState<
    (RaffleV2 & GiveawayStatus) | undefined
  >(undefined);

  const countryCodeFilter: FilterButtonOption[] = useMemo(() => {
    return supportedCountryCodes.map((cc) => ({
      value: cc,
      label: `${getCountryData(cc).name} ${getEmojiFlag(cc)}`,
    }));
  }, [supportedCountryCodes]);

  const { data, isLoading, isError, error } =
    trpcReact.getGiveawayStatuses.useQuery({
      network: Network.AleoMainnet,
      openStatus:
        selectedOpenStatus.length > 0 ? selectedOpenStatus : undefined,
      operatorStatus:
        selectedOperatorStatus.length > 0 ? selectedOperatorStatus : undefined,
      countryCode: selectedCountries.length > 0 ? selectedCountries : undefined,
      sortBy,
      sortOrder,
    });

  const { data: failedData } = trpcReact.getGiveawayStatuses.useQuery({
    network: Network.AleoMainnet,
    showFailed: true,
    sortBy: 'cutoff',
    sortOrder: 'asc',
  });

  const handleOpenStatusChange = (value: string) => {
    const status = value as GiveawayOpenStatus;
    setSelectedOpenStatus((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status]
    );
  };

  const handleOperatorStatusChange = (value: string) => {
    const status = value as GiveawayOperatorStatus;
    setSelectedOperatorStatus((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status]
    );
  };

  const handleCountryChange = (value: string) => {
    const country = value as SupportedCountyCodes;
    setSelectedCountries((prev) =>
      prev.includes(country)
        ? prev.filter((c) => c !== country)
        : [...prev, country]
    );
  };

  const handleSortChange = (field: SortField) => {
    if (field === sortBy) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder(field === 'uniqueEntries' ? 'desc' : 'asc');
    }
  };

  const handleShowGraph = (giveaway: any) => {
    setSelectedGiveaway(giveaway);
  };

  const handleCloseModal = () => {
    setSelectedGiveaway(undefined);
  };

  return (
    <Page>
      <VStack className='w-full gap-4'>
        {(failedData?.length ?? 0) > 0 && (
          <VStack className='gap-2'>
            <Text font='bold-md' color='red'>
              🚨 Failed Giveaways 🚨
            </Text>
            <div className='grid w-full grid-cols-1 justify-items-center gap-4 lg:grid-cols-2'>
              {failedData?.map((giveaway, ix) => {
                const errorMsg = getErrorMessage(giveaway.operatorStatus);
                const isGiveawayClosed = getIsGiveawayClosed(
                  giveaway as RaffleV2
                );
                const isGiveawayOver = getIsGiveawayOver(giveaway as RaffleV2);
                return (
                  <GiveawayCard
                    key={ix}
                    giveaway={giveaway as GiveawayCardProps['giveaway']}
                    errorMsg={errorMsg}
                    isGiveawayClosed={isGiveawayClosed}
                    isGiveawayOver={isGiveawayOver}
                    onShowGraph={() => handleShowGraph(giveaway)}
                  />
                );
              })}
            </div>
          </VStack>
        )}
        <VStack className='gap-2'>
          <Text font='medium-md'>Open Status</Text>
          <div className='flex flex-wrap gap-2'>
            {openStatusFilter.map((option) => (
              <button
                key={option.value}
                onClick={() => handleOpenStatusChange(option.value)}
                className={`rounded-full border px-3 py-1 text-sm ${
                  selectedOpenStatus.includes(
                    option.value as GiveawayOpenStatus
                  )
                    ? 'border-green-500 bg-green-100 text-green-700'
                    : 'border-gray-300 bg-gray-100 text-gray-700'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </VStack>

        <VStack className='gap-2'>
          <Text font='medium-md'>Operator Status</Text>
          <div className='flex flex-wrap gap-2'>
            {operatoorStatusFilter.map((option) => (
              <button
                key={option.value}
                onClick={() => handleOperatorStatusChange(option.value)}
                className={`rounded-full border px-3 py-1 text-sm ${
                  selectedOperatorStatus.includes(
                    option.value as GiveawayOperatorStatus
                  )
                    ? 'border-green-500 bg-green-100 text-green-700'
                    : 'border-gray-300 bg-gray-100 text-gray-700'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </VStack>

        <VStack className='gap-2'>
          <Text font='medium-md'>Country</Text>
          <div className='flex flex-wrap gap-2'>
            {countryCodeFilter.map((option) => (
              <button
                key={option.value}
                onClick={() => handleCountryChange(option.value)}
                className={`rounded-full border px-3 py-1 text-sm ${
                  selectedCountries.includes(
                    option.value as SupportedCountyCodes
                  )
                    ? 'border-green-500 bg-green-100 text-green-700'
                    : 'border-gray-300 bg-gray-100 text-gray-700'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </VStack>

        <VStack className='gap-2'>
          <Text font='medium-md'>Sort By</Text>
          <HStack className='gap-2'>
            <button
              onClick={() => handleSortChange('cutoff')}
              className={`rounded-full border px-3 py-1 text-sm ${
                sortBy === 'cutoff'
                  ? 'border-green-500 bg-green-100 text-green-700'
                  : 'border-gray-300 bg-gray-100 text-gray-700'
              }`}
            >
              Cutoff {sortBy === 'cutoff' && (sortOrder === 'asc' ? '↑' : '↓')}
            </button>
            <button
              onClick={() => handleSortChange('uniqueEntries')}
              className={`rounded-full border px-3 py-1 text-sm ${
                sortBy === 'uniqueEntries'
                  ? 'border-green-500 bg-green-100 text-green-700'
                  : 'border-gray-300 bg-gray-100 text-gray-700'
              }`}
            >
              Unique Entries{' '}
              {sortBy === 'uniqueEntries' && (sortOrder === 'asc' ? '↑' : '↓')}
            </button>
          </HStack>
        </VStack>

        {isLoading && (
          <div className='grid w-full grid-cols-1 justify-items-center gap-4 lg:grid-cols-2'>
            {[...Array(4).keys()].map((ix) => (
              <Skeleton
                className='h-[150px] w-full max-w-xl'
                key={ix}
                delay={ix}
              />
            ))}
          </div>
        )}

        {isError && (
          <VStack className='gap-2'>
            <Text>Error fetching giveaway data</Text>
            {error && <Text>{error.message}</Text>}
          </VStack>
        )}

        <div className='grid w-full grid-cols-1 justify-items-center gap-4 lg:grid-cols-2'>
          {data?.map((giveaway, ix) => {
            const errorMsg = getErrorMessage(giveaway.operatorStatus);
            const isGiveawayClosed = getIsGiveawayClosed(giveaway as RaffleV2);
            const isGiveawayOver = getIsGiveawayOver(giveaway as RaffleV2);
            return (
              <GiveawayCard
                key={ix}
                giveaway={giveaway as GiveawayCardProps['giveaway']}
                errorMsg={errorMsg}
                isGiveawayClosed={isGiveawayClosed}
                isGiveawayOver={isGiveawayOver}
                onShowGraph={() => handleShowGraph(giveaway)}
              />
            );
          })}
        </div>
      </VStack>

      {/* Graph Modal */}
      {selectedGiveaway && (
        <GiveawayEntriesGraphModal
          selectedGiveaway={selectedGiveaway}
          network={Network.AleoMainnet}
          handleCloseModal={handleCloseModal}
        />
      )}
    </Page>
  );
}
